import type { Dispatch as ReduxDispatch } from '@reduxjs/toolkit';
import Cookies from 'cookies-js';

import type { ElasticSearchResponse } from '../@types/ElasticSearchResponse';
import type { Retailer } from '../@types/Retailer';
import type { Vehicle } from '../@types/Vehicle';
import type { BaseAction } from '../@types/actions';
import * as http from '../Http';
import { buildGetRequest } from '../Http/requestTemplates';
import type QueryFilter from '../helpers/query-filter';
import type { RootState } from '../reducers';
import requestCache from '../utils/request-cache';

export const REQ_RETAILER_LIST = 'REQ/RETAILER_LIST';
export const RCV_RETAILER_LIST = 'RCV/RETAILER_LIST';
type REQ_RETAILER_LIST_TYPE = BaseAction<typeof REQ_RETAILER_LIST>;
type RCV_RETAILER_LIST_TYPE = BaseAction<typeof RCV_RETAILER_LIST> & {
  response: ElasticSearchResponse<Retailer>;
};

export const REQ_RETAILER = 'REQ/RETAILER';
export const RCV_RETAILER = 'RCV/RETAILER';
type REQ_RETAILER_TYPE = BaseAction<typeof REQ_RETAILER>;
type RCV_RETAILER_TYPE = BaseAction<typeof RCV_RETAILER> & {
  response: ElasticSearchResponse<Retailer>;
};

export const REQ_RETAILER_SEARCH = 'REQ/RETAILER_SEARCH';
export const RCV_RETAILER_SEARCH = 'RCV/RETAILER_SEARCH';
type REQ_RETAILER_SEARCH_TYPE = BaseAction<typeof REQ_RETAILER_SEARCH> & {
  queryFilter: string;
};
type RCV_RETAILER_SEARCH_TYPE = BaseAction<typeof RCV_RETAILER_SEARCH> & {
  response: ElasticSearchResponse<Retailer>;
  queryFilter: string;
};

export const REQ_RETAILER_VEHICLE_SEARCH = 'REQ/RETAILER_VEHICLE_SEARCH';
export const RCV_RETAILER_VEHICLE_SEARCH = 'RCV/RETAILER_VEHICLE_SEARCH';
type REQ_RETAILER_VEHICLE_SEARCH_TYPE = BaseAction<typeof REQ_RETAILER_VEHICLE_SEARCH> & {
  branchId: string;
};
type RCV_RETAILER_VEHICLE_SEARCH_TYPE = BaseAction<typeof RCV_RETAILER_VEHICLE_SEARCH> & {
  response: ElasticSearchResponse<Vehicle>;
};

export const REQ_CLEAR_RETAILER = 'REQ/CLEAR_RETAILER';
type REQ_CLEAR_RETAILER_TYPE = BaseAction<typeof REQ_CLEAR_RETAILER>;

export const REQUEST_FAILED = 'RETAILER/REQUEST_FAILED';
type REQUEST_FAILED_TYPE = BaseAction<typeof REQUEST_FAILED> & {
  action:
    | typeof REQ_RETAILER_LIST
    | typeof REQ_RETAILER
    | typeof REQ_RETAILER_SEARCH
    | typeof REQ_RETAILER_VEHICLE_SEARCH;
};

export const COOKIE_CENTRAL_STORAGE = 'wayke-central-storage';

export type RETAILER_ACTION =
  | REQ_RETAILER_LIST_TYPE
  | RCV_RETAILER_LIST_TYPE
  | REQ_RETAILER_TYPE
  | RCV_RETAILER_TYPE
  | REQ_RETAILER_SEARCH_TYPE
  | RCV_RETAILER_SEARCH_TYPE
  | REQ_RETAILER_VEHICLE_SEARCH_TYPE
  | RCV_RETAILER_VEHICLE_SEARCH_TYPE
  | REQ_CLEAR_RETAILER_TYPE
  | REQUEST_FAILED_TYPE;

type Dispatch = ReduxDispatch<RETAILER_ACTION>;

export const all = () => async (dispatch: Dispatch, getState: () => RootState) => {
  if (requestCache.isPending(REQ_RETAILER_LIST)) {
    return;
  }

  const { geo } = getState();
  const url = geo?.location
    ? `/api/retailers?filter=${btoa(`q=*&lat=${geo.location.lat}&lon=${geo.location.lng}`)}`
    : '/api/retailers';

  requestCache.isPending(REQ_RETAILER_LIST, true);

  try {
    dispatch({ type: REQ_RETAILER_LIST });

    const response = await http.json<ElasticSearchResponse<Retailer>>(url, buildGetRequest());
    http.validateResponse(response);
    dispatch({
      type: RCV_RETAILER_LIST,
      response: response.response,
    });
  } catch (_e) {
    dispatch({ type: REQUEST_FAILED, action: REQ_RETAILER_LIST });
  }
  requestCache.isPending(REQ_RETAILER_LIST, false);
};

export const find = (slugOrId: string) => async (dispatch: Dispatch) => {
  if (requestCache.isPending(REQ_RETAILER)) {
    return;
  }

  requestCache.isPending(REQ_RETAILER, true);

  try {
    dispatch({ type: REQ_RETAILER });

    const response = await http.json<ElasticSearchResponse<Retailer>>(
      `/api/retailers/${slugOrId}`,
      buildGetRequest(),
    );
    http.validateResponse(response);
    dispatch({
      type: RCV_RETAILER,
      response: response.response,
    });
  } catch (_e) {
    dispatch({ type: REQUEST_FAILED, action: REQ_RETAILER });
  }
  requestCache.isPending(REQ_RETAILER, false);
};

export const findAndSetCookie = (id: string) => (dispatch: Dispatch) => {
  Cookies.set(COOKIE_CENTRAL_STORAGE, id);
  find(id)(dispatch);
};

export const vehicleSearch = (retailer: Retailer, filter: string) => async (dispatch: Dispatch) => {
  if (requestCache.isPending(REQ_RETAILER_VEHICLE_SEARCH)) {
    return;
  }
  requestCache.isPending(REQ_RETAILER_VEHICLE_SEARCH, true);

  const f = filter
    ? `&${filter
        .replace(/retailerId=[^&]+/g, '')
        .replace(/^&|&$/g, '')
        .replace('&&', '&')}`
    : '';

  try {
    dispatch({ type: REQ_RETAILER_VEHICLE_SEARCH, branchId: retailer.id });

    const response = await http.json<ElasticSearchResponse<Vehicle>>(
      `/api/search?branchId=${retailer.id}${f}`,
      buildGetRequest(),
    );
    http.validateResponse(response);
    dispatch({
      type: RCV_RETAILER_VEHICLE_SEARCH,
      response: response.response,
    });
  } catch (_e) {
    dispatch({ type: REQUEST_FAILED, action: REQ_RETAILER_VEHICLE_SEARCH });
  }
  requestCache.isPending(REQ_RETAILER_VEHICLE_SEARCH, false);
};

const getFilterQueryString = (queryFilter: QueryFilter) => {
  if (queryFilter.isEmpty()) return '';

  return `?filter=${queryFilter.toQueryStringFilter()}`;
};

export const retailerSearch = (queryFilter: QueryFilter) => async (dispatch: Dispatch) => {
  requestCache.isPending(REQ_RETAILER_SEARCH, true);

  try {
    dispatch({ type: REQ_RETAILER_SEARCH, queryFilter: queryFilter.toString() });

    const response = await http.json<ElasticSearchResponse<Retailer>>(
      `/api/retailers${getFilterQueryString(queryFilter)}`,
      buildGetRequest(),
    );
    http.validateResponse(response);
    dispatch({
      type: RCV_RETAILER_SEARCH,
      response: response.response,
      queryFilter: queryFilter.toString(),
    });
  } catch (_e) {
    dispatch({ type: REQUEST_FAILED, action: REQ_RETAILER_SEARCH });
  }
  requestCache.isPending(REQ_RETAILER_SEARCH, false);
};

export const clear = () => (dispatch: Dispatch) => dispatch({ type: REQ_CLEAR_RETAILER });
